import React, { useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Alert,
  MenuItem,
  Select,
  FormControl,
  InputLabel
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import { User } from '../../../interfaces/entities/user';
import { UserRoles } from '../../../enums/userRoles.enum';

interface AdminUsersListViewProps {
  users: User[];
  companies: { id: string; name: string }[];
  onResetPassword: (userId: string) => void;
  isResettingPassword: boolean;
}

export const AdminUsersListView: React.FC<AdminUsersListViewProps> = ({
  users,
  companies,
  onResetPassword,
  isResettingPassword
}) => {
  const [companyFilter, setCompanyFilter] = useState('');
  const [firstNameFilter, setFirstNameFilter] = useState('');
  const [resetDialogOpen, setResetDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);

  const filteredUsers = users.filter(user => {
    const matchesCompany = companyFilter === '' || 
      user.companyId === companyFilter;
    const matchesFirstName = firstNameFilter === '' || 
      user.firstName?.toLowerCase().includes(firstNameFilter.toLowerCase());
    return matchesCompany && matchesFirstName;
  });

  const handleResetClick = (user: User) => {
    setSelectedUser(user);
    setResetDialogOpen(true);
  };

  const handleResetConfirm = () => {
    if (selectedUser) {
      onResetPassword(selectedUser.id);
      setResetDialogOpen(false);
      setSelectedUser(null);
    }
  };

  const handleResetCancel = () => {
    setResetDialogOpen(false);
    setSelectedUser(null);
  };

  const getRoleLabel = (role: UserRoles | undefined) => {
    if (role === UserRoles.admin) return 'Admin';
    if (role === UserRoles.owner) return 'Owner';
    if (role === UserRoles.employee) return 'Employee';
    return 'Unknown';
  };

  const getCompanyName = (companyId: string) => {
    const company = companies.find(c => c.id === companyId);
    return company?.name || companyId || '-';
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" sx={{ mb: 3 }}>
        All Users (Admin View)
      </Typography>

      {/* Filters */}
      <Stack direction="row" spacing={2} sx={{ mb: 3 }}>
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>Company</InputLabel>
          <Select
            value={companyFilter}
            label="Company"
            onChange={(e) => setCompanyFilter(e.target.value)}
          >
            <MenuItem value="">All Companies</MenuItem>
            {companies.map((company) => (
              <MenuItem key={company.id} value={company.id}>
                {company.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <TextField
          label="First Name"
          variant="outlined"
          size="small"
          value={firstNameFilter}
          onChange={(e) => setFirstNameFilter(e.target.value)}
          sx={{ minWidth: 200 }}
        />
      </Stack>

      {/* Users Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
              <TableCell sx={{ fontWeight: 600 }}>ID</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>First Name</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Last Name</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Email</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Role</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Company</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Branch ID</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredUsers.map((user) => (
              <TableRow key={user.id} hover>
                <TableCell>{user.id}</TableCell>
                <TableCell>{user.firstName}</TableCell>
                <TableCell>{user.lastName}</TableCell>
                <TableCell>{user.email}</TableCell>
                <TableCell>{getRoleLabel(user.role)}</TableCell>
                <TableCell>{getCompanyName(user.companyId || '')}</TableCell>
                <TableCell>{user.branchId || '-'}</TableCell>
                <TableCell>
                  <IconButton
                    color="primary"
                    onClick={() => handleResetClick(user)}
                    disabled={isResettingPassword}
                    size="small"
                    title="Reset Password"
                  >
                    <RefreshIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {filteredUsers.length === 0 && (
        <Typography sx={{ mt: 2, textAlign: 'center', color: 'text.secondary' }}>
          No users found matching the filters.
        </Typography>
      )}

      {/* Reset Password Confirmation Dialog */}
      <Dialog open={resetDialogOpen} onClose={handleResetCancel} maxWidth="xs" fullWidth>
        <DialogTitle>Reset Password</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            This will reset the user's password to a temporary password.
          </Alert>
          <Typography variant="body2">
            User: <strong>{selectedUser?.firstName} {selectedUser?.lastName}</strong>
            <br />
            Email: <strong>{selectedUser?.email}</strong>
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleResetCancel} color="inherit">
            Cancel
          </Button>
          <Button 
            onClick={handleResetConfirm} 
            variant="contained" 
            color="warning"
            disabled={isResettingPassword}
          >
            {isResettingPassword ? 'Resetting...' : 'Reset Password'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

